import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const QuickBooksSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check URL parameters for success/error status
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const success = searchParams.get('success');
    const companyName = searchParams.get('company');

    if (error) {
      setStatus('error');
      setMessage(errorDescription || 'Failed to connect to QuickBooks');
    } else if (success === 'true') {
      setStatus('success');
      setMessage(companyName ? `Successfully connected to ${companyName}` : 'Successfully connected to QuickBooks');
    } else {
      // Default to success if no error parameter
      setStatus('success');
      setMessage('QuickBooks connection established');
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Processing Connection...
              </h2>
              <p className="text-muted-foreground">
                Please wait while we complete the setup
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto w-16 h-16 bg-success/15 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Connection Successful!
              </h2>
              <p className="text-muted-foreground mb-6">
                {message}
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                Your QuickBooks data will begin syncing shortly. The initial sync may take a few minutes depending on the amount of data.
              </p>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  onClick={() => navigate('/sources/integrations')}
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                  className="w-full"
                >
                  Back to Integrations
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 bg-destructive/15 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-10 h-10 text-destructive" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Connection Failed
              </h2>
              <p className="text-muted-foreground mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  onClick={() => navigate('/sources/integrations')}
                  className="w-full"
                >
                  Try Again
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => window.open('https://developer.intuit.com/app/developer/qbo/docs/get-started', '_blank')}
                  className="w-full"
                >
                  View Documentation
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default QuickBooksSuccessPage; 